import logger from '@/utils/logger';
/**
 * PAGE: PARAMÈTRES UTILISATEUR
 * Interface complète de gestion du compte
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useTokens } from '../hooks/useTokens';
import { supabase } from '@/lib/supabase';
import { DataManager } from '@/lib/data-manager';
import { RealtimeManager } from '@/lib/realtime-manager';
import { useNavigate } from 'react-router-dom';
import { getLoginUrl } from '../utils/domain';
import {
  AccountSection,
  SubscriptionSection,
  BillingSection,
  PreferencesSection,
} from '@/components/settings/SettingsSections';
import PublicLayout from '@/components/layout/PublicLayout';

export default function Settings() {
 const { user, loading: authLoading, signOut } = useAuth();
 const navigate = useNavigate();
 const [activeTab, setActiveTab] = useState('profile');
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

 // TOUS LES HOOKS DOIVENT ÊTRE AVANT LE RETURN EARLY
 // États pour chaque section
 const [profile, setProfile] = useState({
 full_name: '',
 email: '',
 company_name: '',
 company_size: '',
 industry: '',
 role: '',
 phone: '',
 avatar_url: '',
 });

 const [preferences, setPreferences] = useState({
 email_notifications: true,
 marketing_emails: false,
 language: 'fr',
 timezone: 'Europe/Paris',
 });

 // UTILISER useTokens pour la balance en temps réel
 const tokenData = useTokens();

  // Fonction de chargement des données utilisateur
  const loadUserData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Charger le profil avec DataManager (cache-first)
      const profileData = await DataManager.getProfile(user.id);

      if (profileData) {
        logger.log('[Settings] Profile loaded from DataManager');
        setProfile({
          full_name: profileData.full_name || '',
          email: profileData.email || user.email || '',
          company_name: profileData.company_name || '',
          company_size: profileData.company_size || '',
          industry: profileData.industry || '',
          role: profileData.role || '',
          phone: profileData.phone || '',
          avatar_url: profileData.avatar_url || '',
        });

        // Balance tokens vient maintenant de useTokens (temps réel)
        // On ne fait plus setTokenBalance ici, on utilise directement tokenData

        // Préférences
        setPreferences(profileData.preferences || preferences);
      }
    } catch (error) {
      logger.error('[Settings] Erreur chargement profil:', error);
    } finally {
      setLoading(false);
    }
  };

 // Redirection vers login si non connecté
 useEffect(() => {
 if (!authLoading && !user) {
 const loginUrl = getLoginUrl();
 if (loginUrl.startsWith('http')) {
 window.location.href = loginUrl;
 } else {
 navigate(loginUrl);
 }
 }
 }, [authLoading, user, navigate]);

 // Charger les données utilisateur
 useEffect(() => {
 if (user) {
 loadUserData();
 }
 }, [user]);

 const showMessage = (type: 'success' | 'error', text: string) => {
 setMessage({ type, text });
 setTimeout(() => setMessage(null), 5000);
 };

 const tabs = [
 { id: 'profile', label: 'Profil' },
 { id: 'account', label: 'Compte' },
 { id: 'subscription', label: 'Abonnement & Tokens' },
 { id: 'billing', label: 'Facturation' },
 { id: 'preferences', label: 'Préférences' },
 ];

 // Early return si pas d'user
 if (!user || authLoading) {
 return (
 <div className="min-h-screen bg-theme-base flex items-center justify-center">
 <div className="text-center">
 <div className="w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
 <p className="text-theme-secondary">Vérification de l'authentification...</p>
 </div>
 </div>
 );
 }

 return (
 <PublicLayout>
 <div className="pub-wrap pt-10">
 <p className="home-kicker mb-2">Compte</p>
 <h1 className="font-display text-4xl mb-2">Paramètres</h1>
 <button
 onClick={() => navigate('/studio')}
 className="text-sm text-theme-secondary hover:text-theme-primary mb-6"
 >
 ← Studio
 </button>

 <div className="flex gap-0 mt-4 overflow-x-auto border-b border-theme">
 {tabs.map((tab) => (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`
 studio-tab ${activeTab === tab.id ? 'is-active' : ''}
 `}
 >
 {tab.label}
 </button>
 ))}
 </div>

 {/* Message notification */}
 {message && (
 <div className="max-w-7xl mx-auto px-6 pt-4">
 <div className={`
 p-4 rounded-xl border backdrop-blur-sm
 ${message.type === 'success'
 ? 'bg-theme-success/10 border-theme-success/30 text-theme-success'
 : 'bg-theme-error/10 border-theme-error/30 text-theme-error'
 }
 `}>
 {message.text}
 </div>
 </div>
 )}

 {/* Content */}
 <div className="max-w-7xl mx-auto px-6 py-8">
 {activeTab === 'profile' && (
 <ProfileSection 
 profile={profile} 
 setProfile={setProfile} 
 onSave={() => showMessage('success', 'Profil mis à jour avec succès')}
 />
 )}
 
 {activeTab === 'account' && (
 <AccountSection 
 user={user} 
 onSave={() => showMessage('success', 'Compte mis à jour avec succès')}
 onError={(msg) => showMessage('error', msg)}
 />
 )}
 
 {activeTab === 'subscription' && (
              tokenData.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border border-theme-text-primary border-t-transparent mx-auto"></div>
                </div>
              ) : (
 <SubscriptionSection 
 tokenBalance={{
 balance: tokenData.balance,
 totalPurchased: tokenData.totalPurchased,
 totalUsed: tokenData.totalUsed,
 bonus: tokenData.breakdown?.reserve || 0,
 currentPlan: tokenData.subscriptionTier,
 }}
 onNavigate={() => navigate('/billing')}
 />
 )
 )}
 
 {activeTab === 'billing' && (
 <BillingSection user={user} />
 )}
 
 {activeTab === 'preferences' && (
 <PreferencesSection 
 preferences={preferences}
 setPreferences={setPreferences}
 onSave={() => showMessage('success', 'Préférences enregistrées')}
 />
 )}
 </div>
 </div>
 </PublicLayout>
 );
}

// ============================================
// SECTION: PROFIL
// ============================================

interface ProfileSectionProps {
 profile: any;
 setProfile: (profile: any) => void;
 onSave: () => void;
}

function ProfileSection({ profile, setProfile, onSave }: ProfileSectionProps) {
 const { user } = useAuth();
 const [saving, setSaving] = useState(false);

 const handleSave = async () => {
 if (!user) return;

 setSaving(true);
 try {
 // Sauvegarder TOUTES les colonnes du profil Settings
 const { error } = await supabase
 .from('profiles')
 .update({
 full_name: profile.full_name,
 company_name: profile.company_name,
 company_size: profile.company_size,
 industry: profile.industry,
 role: profile.role,
 phone: profile.phone,
 avatar_url: profile.avatar_url, // AJOUTÉ
 updated_at: new Date().toISOString(),
 })
 .eq('id', user.id);

 if (error) throw error;
 
 logger.log(' [Settings] Profile sauvegardé:', {
 user_id: user.id,
 full_name: profile.full_name,
 company_name: profile.company_name,
 industry: profile.industry
 });
 
 onSave();
 } catch (error) {
 logger.error(' [Settings] Erreur sauvegarde profil:', error);
 } finally {
 setSaving(false);
 }
 };

 return (
 <div className="space-y-6">
 {/* Avatar */}
 <div className="p-6 bg-theme-surface border border-theme">
 <h3 className="font-display text-lg mb-4">Photo de profil</h3>
 <div className="flex items-center gap-6">
 <div className="w-16 h-16 border border-theme-strong flex items-center justify-center text-xl font-display">
 {profile.full_name?.charAt(0)?.toUpperCase() || profile.email?.charAt(0)?.toUpperCase() || 'U'}
 </div>
 <div>
 <p className="text-sm text-theme-secondary">Votre initiale est affichée automatiquement</p>
 </div>
 </div>
 </div>

 {/* Informations personnelles */}
 <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
 <h3 className="text-lg font-bold text-theme-primary mb-4">Informations personnelles</h3>
 <div className="grid md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-theme-secondary mb-2">Nom complet</label>
 <input
 type="text"
 value={profile.full_name}
 onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
 className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
 placeholder="John Doe"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-theme-secondary mb-2">Email</label>
 <input
 type="email"
 value={profile.email}
 disabled
              className="w-full px-4 py-3 bg-theme-hover/50 border border-theme-border/50 rounded-xl text-theme-tertiary cursor-not-allowed"
 />
 <p className="text-xs text-theme-tertiary mt-1">L'email ne peut pas être modifié ici</p>
 </div>

 <div>
 <label className="block text-sm font-medium text-theme-secondary mb-2">Téléphone</label>
 <input
 type="tel"
 value={profile.phone}
 onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
 className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
 placeholder="+33 6 12 34 56 78"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-theme-secondary mb-2">Poste</label>
 <input
 type="text"
 value={profile.role}
 onChange={(e) => setProfile({ ...profile, role: e.target.value })}
 className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
 placeholder="CEO, Data Analyst..."
 />
 </div>
 </div>
 </div>

 {/* Entreprise */}
 <div className="p-6 rounded-2xl bg-theme-surface border border-theme-border">
 <h3 className="text-lg font-bold text-theme-primary mb-4">Entreprise</h3>
 <div className="grid md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-theme-secondary mb-2">Nom de l'entreprise</label>
 <input
 type="text"
 value={profile.company_name}
 onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
 className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
 placeholder="Acme Inc."
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-theme-secondary mb-2">Taille de l'entreprise</label>
 <select
 value={profile.company_size}
 onChange={(e) => setProfile({ ...profile, company_size: e.target.value })}
 className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary focus:outline-none focus:border-accent-primary/50 transition-colors"
 >
 <option value="">Sélectionner...</option>
 <option value="1-10">1-10 employés</option>
 <option value="11-50">11-50 employés</option>
 <option value="51-200">51-200 employés</option>
 <option value="201-500">201-500 employés</option>
 <option value="500+">500+ employés</option>
 </select>
 </div>

 <div className="md:col-span-2">
 <label className="block text-sm font-medium text-theme-secondary mb-2">Secteur d'activité</label>
 <input
 type="text"
 value={profile.industry}
 onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
 className="w-full px-4 py-3 bg-theme-surface border border-theme-border rounded-xl text-theme-primary placeholder-theme-tertiary focus:outline-none focus:border-accent-primary/50 transition-colors"
 placeholder="E-commerce, Finance, Tech..."
 />
 </div>
 </div>
 </div>

 {/* Actions */}
 <div className="flex justify-end gap-3">
 <button
 onClick={handleSave}
 disabled={saving}
 className="px-6 py-3 btn-modern btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
 </button>
 </div>
 </div>
 );
}
